/**
 * Bus for "the user must pick a value" requests coming from canvas handles.
 *
 * A handle is drawn on the overlay canvas and cannot render a list of values nor an
 * angle dial, so the handle publishes here and the Vue layer (DynBlockValuePicker)
 * displays the right editor next to it.
 *
 * This is a module-level singleton — like selectionService — so the transform layer
 * never has to depend on Facade or on a manager instance.
 */

/** Which editor the Vue layer should open. */
export type DynBlockPickKind = "list" | "angle";

/** Payload describing which property the user is being asked to set. */
export interface DynBlockPickRequest {
  kind: DynBlockPickKind;
  /** Handle of the block reference the property belongs to. */
  blockHandle: string;
  propertyName: string;
  /** Current value as ODA stores it — radians for an angle. */
  currentValue: string;
  /** Empty for an angle, which is a free scalar rather than a list. */
  allowedValues: string[];
  /** Screen position of the handle, so the picker can open next to it. */
  screenPosition: { x: number; y: number };
}

type PickRequestHandler = (request: DynBlockPickRequest | null) => void;

class DynPickBus {
  private handlers = new Set<PickRequestHandler>();

  /** @returns unsubscribe function */
  subscribe(handler: PickRequestHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** Publish a request, or null to close the picker. */
  emit(request: DynBlockPickRequest | null): void {
    this.handlers.forEach((handler) => handler(request));
  }
}

export const dynPickBus = new DynPickBus();
